import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const UserWelcome = () => {
  const { user, getUserDisplayName, getUserRole } = useAuth();
  const { isMobile, isTablet, getResponsiveValue } = useTheme();

  const getRoleIcon = (role) => {
    const icons = {
      admin: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" 
          />
        </svg>
      ),
      department: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
          />
        </svg>
      )
    };
    return icons[role] || icons.department;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-accent-subtle text-accent',
      department: 'bg-blue-100 text-blue-800'
    };
    return colors[role] || 'bg-surface-secondary text-text-secondary';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      department: 'Department User'
    };
    return labels[role] || 'User';
  };

  const getWelcomeMessage = (role) => {
    const messages = {
      admin: 'Ready to manage the StateSync system today?',
      department: 'Ready to access your department tools?'
    };
    return messages[role] || 'Welcome to StateSync Government Portal';
  };

  const formatLastLogin = (lastLoginAt) => {
    if (!lastLoginAt) return 'First time login';
    
    const now = new Date();
    const loginDate = new Date(lastLoginAt);
    const diffInHours = Math.floor((now - loginDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return loginDate.toLocaleDateString();
  };

  // Extract first name from fullName
  const getFirstName = () => {
    if (!user.fullName) return 'User';
    const names = user.fullName.trim().split(' ');
    return names[0];
  };

  if (!user) return null;

  const userRole = getUserRole();

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    tablet: 'p-5',
    desktop: 'p-6'
  });

  const containerMargin = getResponsiveValue({
    mobile: 'mb-4',
    desktop: 'mb-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    tablet: 'text-2xl',
    desktop: 'text-2xl'
  });

  const subtitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-base'
  });

  const infoTextSize = getResponsiveValue({
    mobile: 'text-xs',
    desktop: 'text-sm'
  });

  const timeTextSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-lg'
  });

  const spacing = getResponsiveValue({
    mobile: 'space-x-2',
    desktop: 'space-x-4'
  });

  return (
    <div className={`bg-gradient-to-r from-navigation to-navigation-hover rounded-2xl ${containerPadding} ${containerMargin}`}>
      <div className={`flex ${isMobile ? 'flex-col space-y-4' : 'items-center justify-between'}`}>
        <div className={`flex items-center ${spacing}`}>
          <div className="flex-1 min-w-0">
            <h1 className={`${titleSize} font-semibold text-navigation-text`}>
              Welcome back, {getFirstName()}!
            </h1>
            <p className={`text-navigation-text opacity-80 mt-1 ${subtitleSize}`}>
              {getWelcomeMessage(userRole)}
            </p>
          </div>
        </div>

        <div className={`${isMobile ? 'text-center' : 'text-right'} text-navigation-text opacity-80`}>
          <div className={`${infoTextSize}`}>Today</div>
          <div className={`${timeTextSize} font-semibold`}>
            {new Date().toLocaleDateString('en-US', { 
              weekday: isMobile ? 'short' : 'long',
              month: 'short', 
              day: 'numeric' 
            })}
          </div>
          <div className={`${infoTextSize}`}>
            {new Date().toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserWelcome;